import { useEffect, useState } from "react";
import { request } from "../api/client";

/** One request at a time; leaving the page, hiding the tab or going offline aborts it. */
export function useResultAnalytics<T>(path: string, revision: number, intervalMillis: number, enabled = true) {
  const [active, setActive] = useState(() => !document.hidden && navigator.onLine);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<unknown>();
  const [read, setRead] = useState<{ data: T; readAt: number; monotonicAt: number }>();
  useEffect(() => {
    const update = () => setActive(!document.hidden && navigator.onLine);
    document.addEventListener("visibilitychange", update); window.addEventListener("online", update); window.addEventListener("offline", update);
    return () => { document.removeEventListener("visibilitychange", update); window.removeEventListener("online", update); window.removeEventListener("offline", update); };
  }, []);
  useEffect(() => {
    const controller = new AbortController(); let timer: ReturnType<typeof setTimeout> | undefined;
    setRead(undefined); setError(undefined); setReading(false);
    if (!path || !active || !enabled) return () => controller.abort();
    const load = async () => {
      if (controller.signal.aborted) return;
      setReading(true);
      let nextDelay = intervalMillis;
      try {
        const data = await request<T>(path, "GET", undefined, controller.signal);
        const suggested = (data as { refreshAfterMillis?: unknown }).refreshAfterMillis;
        if (typeof suggested === "number" && Number.isFinite(suggested)) nextDelay = Math.max(intervalMillis, suggested);
        if (!controller.signal.aborted) { setRead({ data, readAt: Date.now(), monotonicAt: performance.now() }); setError(undefined); }
      } catch (failure) { if (!controller.signal.aborted) { setError(failure); setRead(undefined); } }
      finally {
        if (!controller.signal.aborted) {
          setReading(false);
          if (intervalMillis > 0) timer = setTimeout(() => void load(), nextDelay);
        }
      }
    };
    void load();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [path, revision, intervalMillis, active, enabled]);
  return { read, error, reading, active };
}
